import { ApiResponse } from '../../../shared/types';
import { loadAccessToken } from './storage';

/**
 * 공통 API 클라이언트 유틸리티
 * 백엔드의 공통 응답 구조를 처리합니다.
 */

/**
 * API 요청 옵션
 */
interface ApiRequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean>;
  skipAuth?: boolean; // 인증이 필요 없는 요청인 경우 true
}

/**
 * API 호출 헬퍼 함수
 * 백엔드의 공통 응답 구조를 자동으로 처리합니다.
 */
export async function apiRequest<T>(
  url: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const { params, skipAuth = false, ...fetchOptions } = options;

  // 쿼리 파라미터 추가
  let finalUrl = url;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      finalUrl += (url.includes('?') ? '&' : '?') + queryString;
    }
  }

  // 기본 헤더 설정
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers as Record<string, string>),
  };

  // 인증 토큰 추가 (skipAuth가 false인 경우)
  if (!skipAuth) {
    const accessToken = loadAccessToken();
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
  }

  const response = await fetch(finalUrl, {
    ...fetchOptions,
    headers,
  });

  const responseData: ApiResponse<T> = await response.json();

  // 401 Unauthorized 처리
  if (response.status === 401) {
    // 잠금 메시지인지 확인 (서버에서 반환한 메시지 사용)
    const errorMessage = responseData.message || '인증이 만료되었습니다. 다시 로그인해주세요.';
    const isLocked = errorMessage.includes('🔒') || errorMessage.includes('잠금');
    
    // 잠금이 아닌 경우에만 인증 만료 이벤트 발생
    if (!isLocked) {
      window.dispatchEvent(new CustomEvent('auth-expired'));
    }
    
    // 서버에서 반환한 메시지를 그대로 사용
    throw new Error(errorMessage);
  }

  // 402 Payment Required 처리 (토큰 부족 등)
  if (response.status === 402) {
    const errorMessage = responseData.message || '토큰이 부족합니다';
    const errorData = responseData as any;
    
    // 토큰 부족 정보가 있으면 상세 메시지 추가
    if (errorData.remainingCoins !== undefined) {
      throw new Error(
        `${errorMessage} (보유: ${errorData.remainingCoins}개)`
      );
    }
    
    throw new Error(errorMessage);
  }

  // 403 Forbidden 처리 (IP 차단 등)
  if (response.status === 403) {
    const errorMessage = responseData.message || '접근이 차단되었습니다';
    const errorData = responseData as any;
    
    // IP 차단 정보가 있으면 상세 메시지 추가
    if (errorData.remainingMinutes !== undefined) {
      throw new Error(
        `${errorMessage}\n남은 시간: 약 ${errorData.remainingMinutes}분\n사유: ${errorData.reason || '알 수 없음'}`
      );
    }
    
    throw new Error(errorMessage);
  }

  // 에러 처리
  if (!response.ok || responseData.status === 'error') {
    throw new Error(
      responseData.message || `API 요청 실패: ${response.statusText}`
    );
  }

  // 성공 응답에서 data 반환
  return responseData.data;
}

/**
 * GET 요청
 */
export async function apiGet<T>(
  url: string,
  params?: Record<string, string | number | boolean>,
  options?: Omit<ApiRequestOptions, 'method' | 'params'>
): Promise<T> {
  return apiRequest<T>(url, {
    method: 'GET',
    params,
    ...options,
  });
}

/**
 * POST 요청
 */
export async function apiPost<T>(
  url: string,
  data?: any,
  options?: Omit<ApiRequestOptions, 'body' | 'method'>
): Promise<T> {
  return apiRequest<T>(url, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
    ...options,
  });
}

/**
 * PUT 요청
 */
export async function apiPut<T>(
  url: string,
  data?: any,
  options?: Omit<ApiRequestOptions, 'body' | 'method'>
): Promise<T> {
  return apiRequest<T>(url, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
    ...options,
  });
}

/**
 * PATCH 요청
 */
export async function apiPatch<T>(
  url: string,
  data?: any,
  options?: Omit<ApiRequestOptions, 'body' | 'method'>
): Promise<T> {
  return apiRequest<T>(url, {
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
    ...options,
  });
}

/**
 * DELETE 요청
 */
export async function apiDelete<T>(
  url: string,
  options?: Omit<ApiRequestOptions, 'method'>
): Promise<T> {
  return apiRequest<T>(url, {
    method: 'DELETE',
    ...options,
  });
}
